import React from 'react'
import PropTypes from 'prop-types'

import UnitComponent from 'components/UnitComponent'
import BackLink from 'components/BackLink'
import BreadcrumbHeaderRow from 'components/BreadcrumbHeaderRow'
import CoursePreviewBanner from 'components/CoursePreviewBanner'
import LearnErrorBoundary from 'components/LearnErrorBoundary'
import CourseComponent from 'components/CourseComponent'
import SiteSelector from 'components/SiteSelector'
import TieredInterventionUnitCard from 'components/TieredInterventionUnitCard'
import UnitOverview from 'experienceComponents/UnitOverview'
import { UnitV3TrainingCard } from './UnitV3TrainingCard'
import { HeaderTwo } from 'secondstep-components'

import {
  BannerSpacer,
  UnitV3BodyBackground,
  UnitV3SectionWrapper,
  UnitV3SectionHeader,
  PreviewBannerWrapper,
  UnitV3ContentWrapper,
  UnitV3LessonPageWrapper,
  UnitV3OverviewBox,
  UnitV3SiteSelectorWrapper,
  UnitV3TopNavigation,
  UnitV3PageWrapper,
} from './component.styles'
import {
  getCourseHeaderData,
  getPresentationMode,
  getUnitTabs,
  isLessonPageDisplay,
  prepareItemsForSummaryList,
  prepareTrainingCallout,
  resolveProgramTypeFromContentGroup,
} from './data'
import {
  getBackLinkLabel,
  getGradeDisplayTitleFromMatch,
  getPathForRoute,
  getTrainingPathForRoute,
} from './helper'
import { hasBreadcrumbLinks } from 'utils/breadcrumbHelpers'
import { resolveCurriculumPreviewMode } from 'utils/routing/routeHelpers'

/**
 * UnitV3 renders a unit fetched via curriculum-service v3.
 *
 * When the new `pageDisplayType` toggle is true ("lessonPage"), the page
 * renders link summaries as flattened lesson cards. Otherwise, it falls back
 * to the existing lesson accordion UI via `UnitComponent`.
 */
export function UnitV3PageComponent(props) {
  const {
    unit,
    linkSummaries,
    trainingLinkSummary,
    match,
    location,
    productName,
    navigationEvent,
    onBacklinkClick,
    onSelectUnit,
    isPreview,
    classTitle,
    classInstanceId,
    contentGroupCode,
    instructorDataKey,
    lmsUnit,
    siteId,
  } = props || {}

  // DWB unit URLs end with `:grade` (the grade segment resolves to the unit
  // entry), while legacy K8 unit URLs end with `:unit`. Try both so this
  // page can be reused across either route shape.
  const unitId = match?.params?.unit || match?.params?.grade

  if (!unit) {
    return null
  }

  const queryParamsString = location?.search || ''
  const presentationMode = getPresentationMode(match)
  const showPreviewBanner =
    isPreview || resolveCurriculumPreviewMode({ match, location })
  const hasAccordionTabs = getUnitTabs(unit).length > 0
  const hasLinkSummaries =
    Array.isArray(linkSummaries) && linkSummaries.length > 0
  const useLessonPageLayout =
    !hasAccordionTabs && (isLessonPageDisplay(unit) || hasLinkSummaries)
  const summaryItems = prepareItemsForSummaryList(linkSummaries)
  const lessonsHeaderText =
    summaryItems.length > 0 ? `Lessons 1-${summaryItems.length}` : 'Lessons'
  const { lessonsAmounts, scopeAndSequenceLinks } = getCourseHeaderData({
    unit,
    linkSummaries,
    productName,
  })
  const backLinkLabel = getBackLinkLabel({ contentGroupCode, productName })
  const programType = resolveProgramTypeFromContentGroup(contentGroupCode)

  const renderSummaryItem = function renderSummaryItem({ item, lessonNumber }) {
    return (
      <TieredInterventionUnitCard
        description={item?.description}
        isPreviewMode={showPreviewBanner || (item?.isPreview ?? isPreview)}
        lessonNumber={lessonNumber}
        markedComplete={showPreviewBanner ? false : item?.markedComplete}
        path={getPathForRoute({
          matchUrl: match?.url,
          queryParamsString,
          routeValue: item?.path,
        })}
        programType={programType}
        tags={item?.tags}
        title={item?.title}
      />
    )
  }

  const trainingCallout = prepareTrainingCallout(trainingLinkSummary)

  const gradeDisplayTitle = getGradeDisplayTitleFromMatch(match)
  const courseHeaderTitle = showPreviewBanner
    ? gradeDisplayTitle
    : classTitle || gradeDisplayTitle

  return (
    <LearnErrorBoundary>
      <UnitV3PageWrapper>
        {showPreviewBanner && (
          <PreviewBannerWrapper>
            <BannerSpacer />
            <CoursePreviewBanner />
          </PreviewBannerWrapper>
        )}
        <UnitV3ContentWrapper>
          {hasBreadcrumbLinks(unit.breadcrumb) ? (
            <BreadcrumbHeaderRow
              breadcrumb={unit.breadcrumb}
              currentPageTitle={unit.displayTitle}
            >
              <UnitV3SiteSelectorWrapper>
                <SiteSelector />
              </UnitV3SiteSelectorWrapper>
            </BreadcrumbHeaderRow>
          ) : (
            <UnitV3TopNavigation className="breadcrumbs">
              <BackLink dataTestId="breadcrumb" onClick={onBacklinkClick}>
                {backLinkLabel}
              </BackLink>
              <UnitV3SiteSelectorWrapper>
                <SiteSelector />
              </UnitV3SiteSelectorWrapper>
            </UnitV3TopNavigation>
          )}

          <CourseComponent
            displayTitle={gradeDisplayTitle}
            instance={
              showPreviewBanner ? null : classInstanceId || presentationMode
            }
            lessonsAmounts={lessonsAmounts}
            scopeAndSequenceLinks={scopeAndSequenceLinks}
            skipHead={showPreviewBanner}
            title={courseHeaderTitle}
          >
            {useLessonPageLayout ? (
              <>
                <UnitV3OverviewBox id="unit-overviewbox" role="tabpanel">
                  <UnitOverview
                    contentfulItem={unit}
                    dataTestId={`unit-overview-${unitId}`}
                    tabIndex={0}
                  />
                </UnitV3OverviewBox>
                <UnitV3BodyBackground>
                  {trainingCallout?.route && (
                    <UnitV3SectionWrapper>
                      <UnitV3SectionHeader data-testid="unit-v3-training-heading">
                        <HeaderTwo>Teaching the Unit</HeaderTwo>
                      </UnitV3SectionHeader>
                      <UnitV3TrainingCard
                        contentGroupCode={contentGroupCode}
                        description={trainingCallout.description}
                        instructorDataKey={instructorDataKey}
                        isPreview={showPreviewBanner}
                        path={getTrainingPathForRoute({
                          matchUrl: match?.url,
                          productName,
                          queryParamsString,
                          routeValue: trainingCallout.route,
                        })}
                        siteId={siteId}
                        title={trainingCallout.title}
                        trainingLinkSummary={trainingLinkSummary}
                      />
                    </UnitV3SectionWrapper>
                  )}
                  <UnitV3LessonPageWrapper>
                    <UnitV3SectionHeader data-testid="unit-v3-lessons-heading">
                      <HeaderTwo>{lessonsHeaderText}</HeaderTwo>
                    </UnitV3SectionHeader>
                    {summaryItems.map((item, index) => (
                      <React.Fragment
                        key={`${item?.path ||
                          item?.title ||
                          'lesson-item'}-${index}`}
                      >
                        {renderSummaryItem({ item, lessonNumber: index + 1 })}
                      </React.Fragment>
                    ))}
                  </UnitV3LessonPageWrapper>
                </UnitV3BodyBackground>
              </>
            ) : (
              <UnitComponent
                entryId={unit?.sys?.id}
                key={unitId}
                linkSummaries={linkSummaries}
                lmsUnit={lmsUnit}
                match={match}
                navigationEvent={navigationEvent}
                onSelectUnit={onSelectUnit}
                productName={productName}
                unit={unit}
                unitId={unitId}
              />
            )}
          </CourseComponent>
        </UnitV3ContentWrapper>
      </UnitV3PageWrapper>
    </LearnErrorBoundary>
  )
}

UnitV3PageComponent.propTypes = {
  classInstanceId: PropTypes.string,
  classTitle: PropTypes.string,
  contentGroupCode: PropTypes.string,
  instructorDataKey: PropTypes.string,
  isPreview: PropTypes.bool,
  linkSummaries: PropTypes.array,
  lmsUnit: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      verb: PropTypes.string,
      course: PropTypes.string,
      unit: PropTypes.string,
      grade: PropTypes.string,
    }),
  }),
  navigationEvent: PropTypes.func,
  onBacklinkClick: PropTypes.func,
  onSelectUnit: PropTypes.func,
  productName: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  trainingLinkSummary: PropTypes.object,
  unit: PropTypes.shape({
    breadcrumb: PropTypes.object,
    displayTitle: PropTypes.string,
    description: PropTypes.string,
    pageDisplayType: PropTypes.bool,
    lessons: PropTypes.array,
    sys: PropTypes.object,
  }),
}
